import React, { useCallback, useMemo } from 'react';
import './PanelOverlay.scss';
import {
  PanelComponentKeyType,
  PanelOverlayConfig,
} from 'config/panelConfig';
import PanelOverlayAxis from './PanelOverlayAxis';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { usePanelContainer } from '../usePanelContainer';
import { ReactComponent as MaxIcon } from 'img/max.svg';
import { ReactComponent as MinIcon } from 'img/min.svg';

import { ReactComponent as CW90 } from 'img/clock-rotate-right.svg';
import { ReactComponent as CCW90 } from 'img/clock-rotate-left.svg';
import { ReactComponent as FlipVertical } from 'img/reflect-vertical.svg';
import { ReactComponent as FlipHorizontal } from 'img/reflect-horizontal.svg';
import { scheduleRender } from '../renderState';
import PanelOverlayButton from './PanelOverlayButton';
import { configStore } from 'config/configStore';
import { useSegmentManager } from '@features/analysis/segmentManager';

interface PanelOverlayProps {
  config: PanelOverlayConfig;
  // panelConfig: PanelConfig;
  originComponentKey: PanelComponentKeyType;
  refComponentKey?: PanelComponentKeyType;
  className?: string;
}

const PanelOverlay: React.FC<PanelOverlayProps> = ({
  config,
  originComponentKey,
  refComponentKey,
  className,
}) => {
  //씽글이면 풀스크린 버튼 안보이게 해야
  const { isPreview } = useAnalysisPage();
  const {
    fullscreenPanelComponentKey,
    selectedPanelComponentKey,
    updateFullscreenPanelComponentKey,
    selectedPanelClassName,
    analysisType,
  } = usePanelContainer();

  const componentKey = refComponentKey ? refComponentKey : originComponentKey;
  const isFullscreen = fullscreenPanelComponentKey === componentKey;

  const { mode: manualMode } = useSegmentManager();

  // 누른 패널의 컴포넌트로 풀스크린
  const handleItemClick = useCallback(
    (componentKey?: PanelComponentKeyType) => {
      if (componentKey === fullscreenPanelComponentKey)
        updateFullscreenPanelComponentKey(undefined);
      else updateFullscreenPanelComponentKey(componentKey);
    },
    [fullscreenPanelComponentKey],
  );

  const Contents = useMemo(
    () =>
      Object.entries(config).map(([position, overlay]) => {
        if (!overlay || overlay.visible === false) return null; // <- null 반환

        const contentClassName = React.isValidElement(overlay.content)
          ? String((overlay.content.props as { className?: string })?.className ?? '')
          : '';
        const contentExtraClass = [
          contentClassName.includes('cross-section-metric-overlay') ||
          contentClassName.includes('metric-overlay-hover-shell')
            ? 'has-cross-section-metric'
            : '',
          contentClassName.includes('cross-section-plaque-bar')
            ? 'has-cross-section-plaque-bar'
            : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={position} className={`overlay-content ${position} ${contentExtraClass}`.trim()}>
            {overlay.content}
          </div>
        );
      }),
    [config],
  );

  const isCrossSection = originComponentKey === 'CrossSection';
  const isGraph = originComponentKey === 'Graph';
  const isSelected =
    (!className && selectedPanelComponentKey === componentKey) ||
    (className &&
      selectedPanelComponentKey === componentKey &&
      selectedPanelClassName === className);

  const isCaptureMode  = configStore.get().isCapture;
  // useMemo(()=>{
  //   return (isPreview || (selectedPanelComponentKey === panelConfig.componentKey))
  // },)
  return (
    <div
      key={componentKey}
      className={`panel-overlay ${isSelected && !isCaptureMode ? 'selected' : ''} ${isCrossSection ? 'cross-section-overlay' : ''}`}
    >
      {!(isPreview || isCrossSection || analysisType === 'chamber') &&
        !isCaptureMode && (
          <button
            className={`fullscreenButton ${isFullscreen ? 'off' : ''} ${isGraph ? 'nocolor' : ''}`}
            onClick={() => handleItemClick(originComponentKey)}
          >
            {isFullscreen ? <MinIcon /> : <MaxIcon />}
          </button>
        )}
      {analysisType === 'chamber' &&
        originComponentKey !== 'VR' &&
        originComponentKey !== 'Direction' && (
          <div className="tooltip-wrapper">
            <div className="Frame1000009039">
              <div className="Frame1000009045">
                <button
                  className="btn"
                  onClick={() =>
                    scheduleRender(originComponentKey, {
                      type: 'transformViewport',
                      method: 'rotateCCW90',
                    })
                  }
                >
                  <CCW90 />
                </button>
                <button
                  className="btn"
                  onClick={() =>
                    scheduleRender(originComponentKey, {
                      type: 'transformViewport',
                      method: 'rotateCW90',
                    })
                  }
                >
                  <CW90 />
                </button>
              </div>
              <div className="line137" />
              <div className="Frame1000009045">
                <button
                  className="btn"
                  onClick={() =>
                    scheduleRender(originComponentKey, {
                      type: 'transformViewport',
                      method: 'flipVertical',
                    })
                  }
                >
                  <FlipVertical />
                </button>
                <button
                  className="btn"
                  onClick={() =>
                    scheduleRender(originComponentKey, {
                      type: 'transformViewport',
                      method: 'flipHorizontal',
                    })
                  }
                >
                  <FlipHorizontal />
                </button>
              </div>
            </div>
          </div>
        )}
      {Contents}
      <PanelOverlayAxis componentKey={originComponentKey} />

      <PanelOverlayButton
        componentKey={originComponentKey}
        isOverlayButton={analysisType ==='FFR' &&  !configStore.get().isCapture && !manualMode}
      />
    </div>
  );
};

export default React.memo(PanelOverlay);
